import type { ReactNode, TouchEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@muzly/lib/utils';

import { isRunningInNativeApp, native } from '@muzly/shared';

import {
  MAX_PULL_PX,
  PULL_RESISTANCE,
  PULL_THRESHOLD_PX,
  computePullDistance,
  isPastThreshold,
  pullProgress,
  shouldEngagePull,
} from './pullToRefreshMath';

/**
 * PullToRefresh — 감싼 화면에 "당겨서 새로고침"을 붙이는 래퍼 (#26).
 *
 * 디버그 콘솔이 먼저 썼지만 소비자 화면(홈, 이력의 빈 상태)도 쓴다 — 그래서 `debug`
 * 밖 공용 컴포넌트다.
 *
 * 스크롤 최상단(문서의 `scrollY`≤0)에서 아래로 당기면(수직 터치)
 * 인디케이터를 보여주고, 임계를 넘긴 채 놓으면 `window.location.reload()`로
 * 최신 배포(S3/CloudFront)를 다시 받는다. 터치 이벤트만 듣기에 마우스 환경에선
 * 자연히 비활성이다. 가로 스크럽 그래프(`touch-none`)와는 충돌하지 않는다.
 */

interface PullToRefreshProps {
  children: ReactNode;
  /**
   * 임계 도달 후 놓았을 때 실행. 기본은 최신 배포 재로드 — 그쪽은 화면이 통째로 다시
   * 실리므로 끝을 알릴 필요가 없다. **프로미스를 돌려주면 그것이 끝날 때까지** 스피너가
   * 돈다: 페이지에 남아 다시 읽는 쪽(이력 복원 재시도)이 이 모양이다.
   */
  onRefresh?: () => void | Promise<unknown>;
  /**
   * 감싼 화면이 부모의 높이를 받아야 할 때(`flex-1`) 쓰는 통과용 클래스. 래퍼가 둘(제스처
   * 루트와 당김 변위를 지는 안쪽)이라 **둘 다**에 실어야 높이 사슬이 이어진다.
   */
  className?: string;
}

/**
 * 최신 배포를 다시 받는다. 앱 안에서는 셸에 맡긴다(`reloadApp`) — 셸이 새로고침을 알아야 로딩
 * 오버레이와 딥링크 버퍼를 되돌린다. Android 셸은 실린 뒤의 같은 origin 로드 시작을 문서 안 이동으로
 * 보기 때문에(`isInDocumentNavigation`), 웹이 직접 다시 실으면 그 사이 딥링크가 사라진다.
 */
export const reloadLatestDeploy = (): void => {
  if (isRunningInNativeApp()) {
    native.notify('reloadApp');
    return;
  }
  window.location.reload();
};

export const PullToRefresh = ({
  children,
  onRefresh = reloadLatestDeploy,
  className,
}: PullToRefreshProps) => {
  const startYRef = useRef<number | null>(null);
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // 이 화면이 떠 있는 동안에는 문서의 고무줄 스크롤을 끈다. iOS 웹뷰는 맨 위에서 당기면 문서를
  // 통째로 끌어내리며 웹뷰 바탕(흰색)을 드러내는데, 그 위에 이 컴포넌트의 당김 변위가 겹쳐
  // 흰 띠와 빈 칸이 같이 보였다. 떠날 때 되돌린다 — 다른 화면의 스크롤 느낌은 그대로다.
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.overscrollBehaviorY;
    root.style.overscrollBehaviorY = 'none';
    return () => {
      root.style.overscrollBehaviorY = previous;
    };
  }, []);

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (refreshing) {
      return;
    }
    startYRef.current = window.scrollY <= 0 ? e.touches[0].clientY : null;
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (startYRef.current === null) {
      return;
    }
    const rawDelta = e.touches[0].clientY - startYRef.current;
    // 도중에 스크롤이 내려갔거나 위로 당기면 제스처 취소.
    if (!shouldEngagePull(window.scrollY, rawDelta)) {
      startYRef.current = null;
      setDistance(0);
      return;
    }
    setDistance(
      computePullDistance(rawDelta, {
        resistance: PULL_RESISTANCE,
        maxPx: MAX_PULL_PX,
      }),
    );
  };

  const handleTouchEnd = () => {
    if (startYRef.current === null) {
      return;
    }
    startYRef.current = null;
    if (isPastThreshold(distance, PULL_THRESHOLD_PX)) {
      setRefreshing(true);
      setDistance(PULL_THRESHOLD_PX);
      const running = onRefresh();
      // 화면이 다시 실리는 기본 동작은 돌려줄 것이 없다 — 그때는 스피너를 끄지 않는다
      // (끄면 새 문서가 뜨기 전 한 프레임 동안 아무 일도 없던 것처럼 보인다).
      if (running) {
        void Promise.resolve(running).finally(() => {
          setRefreshing(false);
          setDistance(0);
        });
      }
      return;
    }
    setDistance(0);
  };

  const armed = isPastThreshold(distance, PULL_THRESHOLD_PX);
  const progress = pullProgress(distance, PULL_THRESHOLD_PX);
  const active = refreshing || distance > 0;

  return (
    <div
      className={cn('relative', className)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <div
        // 감싼 화면은 뷰포트 맨 위(상태바 밑)부터 시작한다 — 표시를 안전 영역만큼 내리지 않으면
        // 상태바·다이내믹 아일랜드에 가려 안 보인다.
        className="pointer-events-none absolute inset-x-0 z-20 flex justify-center"
        style={{
          top: 'var(--safe-area-top, 0px)',
          height: distance,
          opacity: active ? 1 : 0,
        }}
        aria-hidden={!active}
      >
        <div
          className="mt-2 flex h-9 w-9 items-center justify-center rounded-full border border-border/60 bg-background text-muted-foreground shadow-sm"
          style={{ opacity: refreshing ? 1 : progress }}
        >
          {refreshing ? (
            <span className="block h-4 w-4 motion-safe:animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
          ) : (
            <span
              className={`text-base leading-none transition-[transform,color] duration-150 ${
                armed ? 'text-primary' : ''
              }`}
              style={{ transform: armed ? 'rotate(180deg)' : 'none' }}
            >
              ↓
            </span>
          )}
        </div>
      </div>

      <div
        className={className}
        style={{
          transform: distance > 0 ? `translateY(${distance}px)` : undefined,
          transition: startYRef.current === null ? 'transform 0.2s' : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
};
